import { useNavigation } from '@/app/hooks'
import { Icon, Text } from '@/components'
import { IconNames } from '@/components/Icon/Icon'
import InnerCard from '@/components/InnerCard/InnerCard'
import classes from '@/utils/classes/classes'

export type Props = {
  title: string
  iconName: IconNames
  children?: React.ReactNode
  articles: { text: string; url: string }[]
}

export type SuggestionCardProps = {
  setLoadedSignal: (loaded: boolean) => void
  articles: Props['articles']
}

function TitleIcon(props: { name: IconNames; className?: string }) {
  return (
    <div
      className={classes(
        'flex p-1.5 bg-themed-purple-100 rounded-lg w-fit h-fit',
        props.className,
      )}
    >
      <Icon name={props.name} size={20} color="base-purple-500" />
    </div>
  )
}

function ArticleSuggestions(props: {
  className?: string
  articles: { text: string; url: string }[]
}) {
  return (
    <ul className={classes('pl-2', props.className)}>
      {props.articles.map((article) => (
        <Text
          variant="link"
          className="text-themed-blue-500 list-item list-disc ml-4"
          // TODO open article preview thingy
          onClick={() => window.open(article.url, '_blank')}
        >
          {article.text}
        </Text>
      ))}
    </ul>
  )
}

export default function DeviceSuggestionCard(props: Props) {
  return (
    <InnerCard>
      <div className="flex flex-row items-center mb-4">
        <TitleIcon name={props.iconName} className="mr-2" />
        <Text variant="body1">{props.title}</Text>
      </div>
      {props.children && (
        <>
          <Text variant="body1" className="mb-2">
            Device Settings:
          </Text>
          <div className="mb-4">{props.children}</div>
        </>
      )}
      {props.articles.length > 0 && (
        <>
          <Text variant="body1" className="mb-2">
            Helpful Articles:
          </Text>
          <ArticleSuggestions articles={props.articles} />
        </>
      )}
    </InnerCard>
  )
}

function SettingsSuggestions(props: {
  className?: string
  children: React.ReactNode
}) {
  return (
    <div className={classes('flex flex-col gap-2', props.className)}>
      {props.children}
    </div>
  )
}
SettingsSuggestions.displayName = 'SettingsSuggestions'
DeviceSuggestionCard.SettingsSuggestions = SettingsSuggestions

function SuggestionCard(props: {
  className?: string
  iconName: IconNames
  title: string | React.ReactNode
  children: React.ReactNode
  destinationPath: string
}) {
  const navigation = useNavigation()
  return (
    <div
      className={classes(
        'rounded-lg bg-themed-grey-100 p-2 cursor-pointer hover:bg-themed-grey-200 transition-colors',
        props.className,
      )}
      onClick={() => navigation.push(props.destinationPath)}
    >
      <div className="flex flex-row">
        <div className="flex-none mr-2">
          <Icon name={props.iconName} size={20} color="base-grey-900" />
        </div>
        <div className="flex-1">
          {typeof props.title === 'string' ? (
            <Text
              variant="body3"
              className="font-bold text-themed-grey-900 mb-1"
            >
              {props.title}
            </Text>
          ) : (
            props.title
          )}
          {props.children}
        </div>
        <div className="flex-none flex items-center justify-end ml-2">
          <Icon name="ChevronRight" color="grey-400" size={16} />
        </div>
      </div>
    </div>
  )
}
SuggestionCard.displayName = 'SuggestionCard'
DeviceSuggestionCard.SuggestionCard = SuggestionCard

function SuggestionDescription(props: {
  children: React.ReactNode
  className?: string
}) {
  return (
    <Text
      variant="body3"
      className={classes('text-themed-grey-500 font-medium', props.className)}
    >
      {props.children}
    </Text>
  )
}
SuggestionDescription.displayName = 'SuggestionDescription'
DeviceSuggestionCard.SuggestionDescription = SuggestionDescription
